import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { RadioButton } from 'primereact/radiobutton';
import { InputSwitch } from 'primereact/inputswitch';
import { LayoutContext } from './context/layoutcontext';
import { classNames } from 'primereact/utils';
import { isLendingNetworkPath } from '../components/MobileBottomNav';

const AppConfig = (props = {}) => {
    const { layoutConfig, layoutState, onConfigUpdate, showConfigSidebar, hideConfigSidebar } = useContext(LayoutContext);
    const location = useLocation();

    // MobileBottomNav renders only on Lending Network routes and only below
    // 992px. Where it's on screen the config button has to sit above it —
    // .has-bottom-nav drives that offset in _config.scss.
    const hasBottomNav = isLendingNetworkPath(location.pathname);

    // Debug: Check if menuTheme exists
    React.useEffect(() => {
        console.log('Current layoutConfig:', layoutConfig);
        console.log('Menu Theme:', layoutConfig.menuTheme);
    }, [layoutConfig]);

    const themes = [
        { name: 'Light', value: 'light', icon: 'pi pi-sun' },
        { name: 'Dim', value: 'dim', icon: 'pi pi-star-fill' },
        { name: 'Dark', value: 'dark', icon: 'pi pi-moon' }
    ];

    const menuModes = [
        { name: 'Static', value: 'static' },
        { name: 'Overlay', value: 'overlay' },
        { name: 'Slim', value: 'slim' },
        { name: 'Compact', value: 'compact' },
        { name: 'Horizontal', value: 'horizontal' },
        { name: 'Reveal', value: 'reveal' },
        { name: 'Drawer', value: 'drawer' }
    ];

    const menuThemes = [
        { name: 'White', value: 'white', color: '#ffffff' },
        { name: 'Dark Blue', value: 'darkblue', color: '#0C1E35' },
        { name: 'Blue', value: 'blue', color: '#2196F3' },
        { name: 'Blue Gray', value: 'bluegray', color: '#455A64' },
        { name: 'Brown', value: 'brown', color: '#785548' },
        { name: 'Cyan', value: 'cyan', color: '#00BCD4' },
        { name: 'Green', value: 'green', color: '#4CAF50' },
        { name: 'Indigo', value: 'indigo', color: '#3F51B5' },
        { name: 'Deep Purple', value: 'deeppurple', color: '#673AB7' },
        { name: 'Orange', value: 'orange', color: '#FF5722' },
        { name: 'Pink', value: 'pink', color: '#E91E63' },
        { name: 'Purple', value: 'purple', color: '#9C27B0' },
        { name: 'Teal', value: 'teal', color: '#009688' }
    ];

    const componentThemes = [
        { name: 'Blue', value: 'blue', color: '#2196F3' },
        { name: 'Green', value: 'green', color: '#4CAF50' },
        { name: 'Light Green', value: 'lightgreen', color: '#8BC34A' },
        { name: 'Purple', value: 'purple', color: '#9C27B0' },
        { name: 'Deep Purple', value: 'deeppurple', color: '#673AB7' },
        { name: 'Indigo', value: 'indigo', color: '#3F51B5' },
        { name: 'Orange', value: 'orange', color: '#FF5722' },
        { name: 'Cyan', value: 'cyan', color: '#00BCD4' },
        { name: 'Pink', value: 'pink', color: '#E91E63' },
        { name: 'Teal', value: 'teal', color: '#009688' }
    ];

    const scales = [12, 13, 14, 15, 16];

    const onColorSchemeChange = (value) => {
        onConfigUpdate({ ...layoutConfig, colorScheme: value });
    };

    const onMenuModeChange = (value) => {
        onConfigUpdate({ ...layoutConfig, menuMode: value });
    };

    const onInputStyleChange = (value) => {
        onConfigUpdate({ ...layoutConfig, inputStyle: value });
    };

    const onRippleChange = (value) => {
        onConfigUpdate({ ...layoutConfig, ripple: value });
    };

    const onMenuThemeChange = (value) => {
        onConfigUpdate({ ...layoutConfig, menuTheme: value });
    };

    const onScaleChange = (value) => {
        onConfigUpdate({ ...layoutConfig, scale: value });
    };

    const onComponentThemeChange = (value) => {
        // Update the theme based on color scheme and component theme
        const colorScheme = layoutConfig.colorScheme;
        const theme = `lara-${colorScheme}-${value}`;
        onConfigUpdate({ ...layoutConfig, theme: theme, componentTheme: value });
    };

    return (
        <div id="layout-config">
            {/* Settings button — shown at every width. It used to be `hidden
                md:flex`, which put theme/layout settings out of reach on a
                phone entirely. */}
            <button
                type="button"
                className={classNames('layout-config-button', { 'has-bottom-nav': hasBottomNav })}
                onClick={showConfigSidebar}
                aria-label="Open settings"
            >
                <i className="pi pi-cog"></i>
            </button>

            <Sidebar
                visible={layoutState.configSidebarVisible}
                position="right"
                onHide={hideConfigSidebar}
            >
                <div className={`w-full sm:w-18rem`} style={{ transition: '.3s cubic-bezier(0, 0, 0.2, 1)' }}>
                    <h5>Color Scheme</h5>
                    <div className="flex">
                        <div className="field-radiobutton flex-auto">
                            <RadioButton name="colorScheme" value="light" checked={layoutConfig.colorScheme === 'light'} inputId="theme3" onChange={(e) => onColorSchemeChange(e.value)}></RadioButton>
                            <label htmlFor="theme3">Light</label>
                        </div>
                        <div className="field-radiobutton flex-auto">
                            <RadioButton name="colorScheme" value="dim" checked={layoutConfig.colorScheme === 'dim'} inputId="theme2" onChange={(e) => onColorSchemeChange(e.value)}></RadioButton>
                            <label htmlFor="theme2">Dim</label>
                        </div>
                        <div className="field-radiobutton flex-auto">
                            <RadioButton name="colorScheme" value="dark" checked={layoutConfig.colorScheme === 'dark'} inputId="theme1" onChange={(e) => onColorSchemeChange(e.value)}></RadioButton>
                            <label htmlFor="theme1">Dark</label>
                        </div>
                    </div>

                    <hr />
                    {!props.minimal && (
                        <>
                            <h5>Menu Type</h5>
                            <div className="flex flex-wrap row-gap-3">
                                <div className="flex align-items-center gap-2 w-6">
                                    <RadioButton name="menuMode" value="static" checked={layoutConfig.menuMode === 'static'} inputId="mode1" onChange={(e) => onMenuModeChange(e.value)}></RadioButton>
                                    <label htmlFor="mode1">Static</label>
                                </div>
                                <div className="flex align-items-center gap-2 w-6">
                                    <RadioButton name="menuMode" value="overlay" checked={layoutConfig.menuMode === 'overlay'} inputId="mode2" onChange={(e) => onMenuModeChange(e.value)}></RadioButton>
                                    <label htmlFor="mode2">Overlay</label>
                                </div>
                                <div className="flex align-items-center gap-2 w-6">
                                    <RadioButton name="menuMode" value="slim" checked={layoutConfig.menuMode === 'slim'} inputId="mode3" onChange={(e) => onMenuModeChange(e.value)}></RadioButton>
                                    <label htmlFor="mode3">Slim</label>
                                </div>
                                <div className="flex align-items-center gap-2 w-6">
                                    <RadioButton name="menuMode" value="compact" checked={layoutConfig.menuMode === 'compact'} inputId="mode4" onChange={(e) => onMenuModeChange(e.value)}></RadioButton>
                                    <label htmlFor="mode4">Compact</label>
                                </div>
                                <div className="flex align-items-center gap-2 w-6">
                                    <RadioButton name="menuMode" value="horizontal" checked={layoutConfig.menuMode === 'horizontal'} inputId="mode5" onChange={(e) => onMenuModeChange(e.value)}></RadioButton>
                                    <label htmlFor="mode4">Horizontal</label>
                                </div>
                                <div className="flex align-items-center gap-2 w-6">
                                    <RadioButton name="menuMode" value="reveal" checked={layoutConfig.menuMode === 'reveal'} inputId="mode6" onChange={(e) => onMenuModeChange(e.value)}></RadioButton>
                                    <label htmlFor="mode5">Reveal</label>
                                </div>
                                <div className="flex align-items-center gap-2 w-6">
                                    <RadioButton name="menuMode" value="drawer" checked={layoutConfig.menuMode === 'drawer'} inputId="mode7" onChange={(e) => onMenuModeChange(e.value)}></RadioButton>
                                    <label htmlFor="mode6">Drawer</label>
                                </div>
                            </div>
                            <hr />
                            <h5>Menu Themes</h5>
                            <div className="flex flex-wrap gap-2">
                                {menuThemes.map((theme) => (
                                    <button
                                        key={theme.value}
                                        type="button"
                                        className={classNames('w-2rem h-2rem border-circle border-none cursor-pointer', {
                                            'outline': layoutConfig.menuTheme === theme.value
                                        })}
                                        onClick={() => onMenuThemeChange(theme.value)}
                                        style={{ backgroundColor: theme.color }}
                                        title={theme.name}
                                    >
                                        {layoutConfig.menuTheme === theme.value && (
                                            <i className="pi pi-check text-white"></i>
                                        )}
                                    </button>
                                ))}
                            </div>
                            <hr />
                        </>
                    )}

                    <h5>Component Themes</h5>
                    <div className="flex flex-wrap gap-2">
                        {componentThemes.map((theme) => (
                            <button
                                key={theme.value}
                                type="button"
                                className={classNames('w-2rem h-2rem border-circle border-none cursor-pointer', {
                                    'outline': (layoutConfig.componentTheme || 'blue') === theme.value
                                })}
                                onClick={() => onComponentThemeChange(theme.value)}
                                style={{ backgroundColor: theme.color }}
                                title={theme.name}
                            >
                                {(layoutConfig.componentTheme || 'blue') === theme.value && (
                                    <i className="pi pi-check text-white"></i>
                                )}
                            </button>
                        ))}
                    </div>
                    <hr />

                    <h5>Scale</h5>
                    <div className="flex align-items-center">
                        <Button text rounded icon="pi pi-minus" onClick={() => onScaleChange(layoutConfig.scale - 1)} className=" w-2rem h-2rem mr-2" disabled={layoutConfig.scale === scales[0]}></Button>
                        <div className="flex gap-2 align-items-center">
                            {scales.map((s, i) => {
                                return (
                                    <i
                                        key={i}
                                        className={classNames('pi pi-circle-fill text-300', {
                                            'text-primary-500': s === layoutConfig.scale
                                        })}
                                    ></i>
                                );
                            })}
                        </div>
                        <Button text rounded icon="pi pi-plus" onClick={() => onScaleChange(layoutConfig.scale + 1)} className=" w-2rem h-2rem ml-2" disabled={layoutConfig.scale === scales[scales.length - 1]}></Button>
                    </div>

                    <hr />

                    {!props.minimal && (
                        <>
                            <h5>Input Style</h5>
                            <div className="flex">
                                <div className="field-radiobutton flex-1">
                                    <RadioButton inputId="input_outlined" name="inputstyle" value="outlined" checked={layoutConfig.inputStyle === 'outlined'} onChange={(e) => onInputStyleChange(e.value)} />
                                    <label htmlFor="input_outlined">Outlined</label>
                                </div>
                                <div className="field-radiobutton flex-1">
                                    <RadioButton inputId="input_filled" name="inputstyle" value="filled" checked={layoutConfig.inputStyle === 'filled'} onChange={(e) => onInputStyleChange(e.value)} />
                                    <label htmlFor="input_filled">Filled</label>
                                </div>
                            </div>
                            <hr />
                        </>
                    )}

                    <h5>Ripple Effect</h5>
                    <InputSwitch checked={layoutConfig.ripple} onChange={(e) => onRippleChange(e.value)} />
                </div>
            </Sidebar>
        </div>
        // <>
        //     <Button
        //         icon="pi pi-cog"
        //         className="layout-config-button p-button-primary"
        //         onClick={showConfigSidebar}
        //         aria-label="Configuration"
        //     />

        //     <Sidebar
        //         visible={layoutState.configSidebarVisible}
        //         onHide={hideConfigSidebar}
        //         position="right"
        //         className="layout-config-sidebar"
        //         modal={false}
        //         style={{ width: '280px' }}
        //     >
        //         <div className="layout-config-content">
        //             {/* Color Scheme */}
        //             <div className="config-section compact">
        //                 <h6>Color Scheme</h6>
        //                 <div className="flex gap-2">
        //                     {themes.map((theme) => (
        //                         <button
        //                             key={theme.value}
        //                             type="button"
        //                             className={classNames('layout-config-button-sm', {
        //                                 'active': layoutConfig.colorScheme === theme.value
        //                             })}
        //                             onClick={() => onColorSchemeChange(theme.value)}
        //                         >
        //                             <i className={theme.icon}></i>
        //                             <span>{theme.name}</span>
        //                         </button>
        //                     ))}
        //                 </div>
        //             </div>


        //             {/* Menu Themes */}
        //             <div className="config-section compact">
        //                 <h6>Menu Themes</h6>
        //                 <div className="color-palette-grid">
        //                     {menuThemes.map((theme) => (
        //                         <button
        //                             key={theme.value}
        //                             type="button"
        //                             className={classNames('color-palette-button', {
        //                                 'active': (layoutConfig.menuTheme || 'colorScheme') === theme.value
        //                             })}
        //                             onClick={() => onMenuThemeChange(theme.value)}
        //                             style={{ backgroundColor: theme.color }}
        //                             title={theme.name}
        //                         >
        //                             {(layoutConfig.menuTheme || 'colorScheme') === theme.value && (
        //                                 <i className="pi pi-check"></i>
        //                             )}
        //                         </button>
        //                     ))}
        //                 </div>
        //             </div>

        //             {/* Component Themes */}
        //             <div className="config-section compact">
        //                 <h6>Component Themes</h6>
        //                 <div className="color-palette-grid">
        //                     {componentThemes.map((theme) => (
        //                         <button
        //                             key={theme.value}
        //                             type="button"
        //                             className={classNames('color-palette-button', {
        //                                 'active': (layoutConfig.componentTheme || 'blue') === theme.value
        //                             })}
        //                             onClick={() => onComponentThemeChange(theme.value)}
        //                             style={{ backgroundColor: theme.color }}
        //                             title={theme.name}
        //                         >
        //                             {(layoutConfig.componentTheme || 'blue') === theme.value && (
        //                                 <i className="pi pi-check"></i>
        //                             )}
        //                         </button>
        //                     ))}
        //                 </div>
        //             </div>

        //             {/* Scale */}
        //             <div className="config-section compact">
        //                 <h6>Scale</h6>
        //                 <div className="scale-slider">
        //                     <button 
        //                         className="scale-button"
        //                         onClick={() => layoutConfig.scale > 12 && onScaleChange(layoutConfig.scale - 1)}
        //                     >
        //                         <i className="pi pi-minus"></i>
        //                     </button>
        //                     <div className="scale-display">
        //                         {scales.map((scale) => (
        //                             <span
        //                                 key={scale}
        //                                 className={classNames('scale-dot', {
        //                                     'active': layoutConfig.scale === scale
        //                                 })}
        //                                 onClick={() => onScaleChange(scale)}
        //                             ></span>
        //                         ))}
        //                     </div>
        //                     <button 
        //                         className="scale-button"
        //                         onClick={() => layoutConfig.scale < 16 && onScaleChange(layoutConfig.scale + 1)}
        //                     >
        //                         <i className="pi pi-plus"></i>
        //                     </button>
        //                 </div>
        //             </div>

        //             {/* Input Style */}
        //             <div className="config-section compact">
        //                 <h6>Input Style</h6>
        //                 <div className="flex gap-2">
        //                     <div className="field-radiobutton flex-1">
        //                         <RadioButton
        //                             inputId="outlined"
        //                             name="inputStyle"
        //                             value="outlined"
        //                             checked={layoutConfig.inputStyle === 'outlined'}
        //                             onChange={(e) => onInputStyleChange(e.value)}
        //                         />
        //                         <label htmlFor="outlined">Outlined</label>
        //                     </div>
        //                     <div className="field-radiobutton flex-1">
        //                         <RadioButton
        //                             inputId="filled"
        //                             name="inputStyle"
        //                             value="filled"
        //                             checked={layoutConfig.inputStyle === 'filled'}
        //                             onChange={(e) => onInputStyleChange(e.value)}
        //                         />
        //                         <label htmlFor="filled">Filled</label>
        //                     </div>
        //                 </div>
        //             </div>

        //             {/* Ripple Effect */}
        //             <div className="config-section compact">
        //                 <h6>Ripple Effect</h6>
        //                 <InputSwitch
        //                     checked={layoutConfig.ripple}
        //                     onChange={(e) => onRippleChange(e.value)}
        //                 />
        //             </div>
        //         </div>
        //     </Sidebar>
        // </>
    );
};

export default AppConfig;